// Copy CENTRALIZADA de la experiencia de rollout hacia el MERCHANT (comercio).
//
// REGLA TRANSVERSAL (decisión de producto): lenguaje técnico CERO. Nunca decir
// "experimento", "prueba", "beta", "variante", "A/B", "control/treatment", "rollout".
// Solo "versión nueva" / "versión anterior". Este archivo es la ÚNICA fuente de verdad
// de los textos visibles: editar aquí, no en los componentes. El equipo de producto da
// el OK final sobre la copy.
//
// Estructura: un objeto por superficie. Las claves describen el contexto, los valores son
// el texto literal que ve el comercio.

// ── A. EvacuatedNotice (reason-aware) ──────────────────────────────────────────────
// El operador regresó este comercio a la versión anterior. El motivo viaja en el contrato
// feature-version como `evac_reason`. Dos variantes de copy + un default robusto.
export const EVACUATED_NOTICE = {
  // A1 — pausa temporal (evac_reason='iterating'). También es el DEFAULT cuando el campo no
  // viene (degradación: el backend lo agrega en paralelo). Tono tranquilizador, reversible.
  iterating: {
    title: 'Volvimos a la versión anterior de tu menú por ahora',
    body: 'Estamos afinando unas mejoras y te avisamos cuando la nueva esté lista otra vez. Tu información sigue intacta.',
  },
  // B2 — descontinuado (evac_reason='discontinued'). Cierre amable, sin promesa de regreso.
  discontinued: {
    title: 'Tu menú vuelve a la versión anterior',
    body: 'Gracias por acompañarnos probando la nueva.',
  },
} as const;

// ── B. Banner de anuncio GA (C2) ───────────────────────────────────────────────────
// El comercio ya está graduado (ga_forced): la versión nueva es la oficial. Banner tipo
// "nueva funcionalidad", descartable y no intrusivo.
export const GA_ANNOUNCEMENT = {
  title: 'Estrenamos una nueva versión de Mi Menú',
  body: 'Más cómoda desde el celular y mejor organizada en tablet y computadora. Ya está activa en tu cuenta.',
  dismiss: 'Entendido',
  dismissAria: 'Descartar aviso',
} as const;

// ── C. Prompt de feedback al revertir (D1) ─────────────────────────────────────────
// Al volver a la versión anterior, preguntamos (omitible) por qué. El motivo viaja en la
// telemetría del revert (fire-and-forget). NUNCA bloquea el revert.
export const REVERT_FEEDBACK = {
  title: '¿Nos cuentas por qué volviste a la versión anterior?',
  subtitle: 'Tu respuesta nos ayuda a mejorar. Es opcional.',
  // Los `value` son estables (van en el payload de telemetría, no se traducen); los `label`
  // son la copy visible. Mantener el lenguaje no técnico.
  options: [
    { value: 'hard_to_use', label: 'Me costó usarla' },
    { value: 'missing_feature', label: 'Me faltaba algo' },
    { value: 'prefer_previous', label: 'Prefiero la anterior' },
    { value: 'other', label: 'Otro' },
  ],
  textPlaceholder: 'Cuéntanos un poco más (opcional)',
  submit: 'Enviar',
  skip: 'Omitir',
  closeAria: 'Cerrar',
  thanks: '¡Gracias por tu opinión!',
} as const;

export type RevertFeedbackReason = (typeof REVERT_FEEDBACK.options)[number]['value'];

// ── D. CTA por ROL (Item E #1) ─────────────────────────────────────────────────────────
// El root decide el default per-comercio (switch durable); el operador no-root solo "echa un
// vistazo" (preview per-usuario, no toca el comercio). La copy del CTA cambia por rol EN RENDER.
export const ROLE_CTA = {
  // ROOT: cambia la versión activa para TODO el comercio (acción durable, reversible).
  rootTry: 'Cambiar a la versión nueva',
  // NO-ROOT: vista previa personal, sin cambiar nada para el comercio.
  operatorPreview: 'Echa un vistazo',
} as const;

// ── E. Barra de vista previa per-usuario (Item E #2) ───────────────────────────────────
// Mientras el operador mira la versión nueva en preview, una barra le recuerda que es solo para
// él y que el comercio sigue en la anterior. "Volver a la actual" apaga el preview (no revierte
// nada del comercio).
export const PREVIEW_BAR = {
  label: 'Estás viendo una vista previa · para tu comercio sigue la anterior',
  back: 'Volver a la actual',
  backAria: 'Salir de la vista previa y volver a la versión actual',
} as const;

// ── F. Prompt de feedback por USO (Item E #3) ──────────────────────────────────────────
// Tras N acciones reales en la versión nueva (vista previa o adoptada), preguntamos —OMITIBLE—
// qué le pareció. Reusa los CHIPS de REVERT_FEEDBACK (mismos motivos), pero el encuadre es
// "qué te pareció", no "por qué volviste". El motivo viaja por telemetría (behavior usage_feedback)
// con by_user_id. Omitible y con opt-out durable ("No volver a preguntar").
export const USAGE_FEEDBACK = {
  title: '¿Qué te parece la versión nueva?',
  subtitle: 'La estuviste usando un rato. Tu opinión nos ayuda a mejorarla. Es opcional.',
  optOut: 'No volver a preguntar',
} as const;
